import { useState, useEffect, useCallback } from "react";
import { Loader2, Inbox, Activity, Eye, Target, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import api from "../../lib/api";

function iso(d) { return d.toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); }

const PRESETS = [
  { label: "7 days", days: 6 },
  { label: "30 days", days: 29 },
  { label: "90 days", days: 89 },
];

const EVENT_LABELS = {
  form_submit: "Form submits", asset_download: "Downloads", calculator_complete: "Calculator completions",
  calculator_open: "Calculator opens", collateral_view: "Collateral views", page_engagement: "Page engagement",
  email_open: "Email opens", email_click: "Email clicks", email_delivered: "Emails delivered",
  email_bounced: "Bounces", email_sent: "Emails sent",
};

export default function RangeAnalytics() {
  const [start, setStart] = useState(daysAgo(29));
  const [end, setEnd] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/range", { params: { start, end } });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load analytics"); }
    finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (days) => { setStart(daysAgo(days)); setEnd(iso(new Date())); };

  const STAT = data ? [
    { icon: Inbox, label: "Leads", value: data.totals.leads },
    { icon: Activity, label: "Engagement events", value: data.totals.events },
    { icon: Eye, label: "Unique visitors", value: data.totals.unique_visitors },
    { icon: Target, label: "Conversion rate", value: `${data.totals.conversion_rate}%`, sub: "leads ÷ unique visitors" },
  ] : [];

  return (
    <div data-testid="range-analytics">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">From</label>
          <input type="date" data-testid="range-start" value={start} max={end} onChange={(e) => setStart(e.target.value)}
            className="bg-ink border border-white/15 px-3 py-2 text-sm text-white focus:border-flame focus:outline-none" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">To</label>
          <input type="date" data-testid="range-end" value={end} min={start} max={iso(new Date())} onChange={(e) => setEnd(e.target.value)}
            className="bg-ink border border-white/15 px-3 py-2 text-sm text-white focus:border-flame focus:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          {PRESETS.map((p) => (
            <button key={p.label} data-testid={`preset-${p.days}`} onClick={() => applyPreset(p.days)}
              className="border border-white/15 text-white/60 hover:text-flame hover:border-flame/50 px-3 py-2 text-sm transition-colors">
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="p-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
            {STAT.map((s) => (
              <div key={s.label} className="bg-ink border border-white/10 p-6" data-testid={`stat-${s.label}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="h-5 w-5 text-flame" />
                  <span className="font-display font-black text-3xl">{s.value}</span>
                </div>
                <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">{s.label}</div>
                {s.sub && <div className="text-[11px] text-white/30 mt-1 normal-case tracking-normal">{s.sub}</div>}
              </div>
            ))}
          </div>

          {/* Trend chart */}
          <div className="bg-ink border border-white/10 p-6 mb-6">
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-white/70 mb-4">Daily trends</h3>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={data.series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="leads" name="Leads" stroke="#ea580c" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="events" name="Engagement" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="views" name="Page views" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Breakdown lists */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="bg-ink border border-white/10 p-6">
              <h3 className="font-display font-bold text-sm uppercase tracking-widest text-white/70 mb-4">Engagement by type</h3>
              <div className="space-y-2">
                {Object.keys(data.events_by_type).length === 0 && <div className="text-sm text-white/40">No events in this range.</div>}
                {Object.entries(data.events_by_type).map(([t, n]) => (
                  <div key={t} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                    <span className="text-white/70">{EVENT_LABELS[t] || t}</span>
                    <span className="font-mono text-flame">{n}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-ink border border-white/10 p-6">
              <h3 className="font-display font-bold text-sm uppercase tracking-widest text-white/70 mb-4 flex items-center gap-2"><Download className="h-4 w-4 text-flame" /> Top downloads</h3>
              <div className="space-y-2">
                {data.top_downloads.length === 0 && <div className="text-sm text-white/40">No downloads in this range.</div>}
                {data.top_downloads.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 gap-3">
                    <span className="text-white/70 truncate">{d.label}</span>
                    <span className="font-mono text-white/50 flex-shrink-0">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-ink border border-white/10 p-6">
              <h3 className="font-display font-bold text-sm uppercase tracking-widest text-white/70 mb-4 flex items-center gap-2"><FileText className="h-4 w-4 text-flame" /> Top pages</h3>
              <div className="space-y-2">
                {data.top_pages.length === 0 && <div className="text-sm text-white/40">No page views in this range.</div>}
                {data.top_pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 gap-3">
                    <span className="text-white/70 truncate">{p.path}</span>
                    <span className="font-mono text-white/50 flex-shrink-0">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
